import { Switch } from '@dnb/eufemia';
import { dereference } from '@portals/shared/common/open-api/resolver';
import { useLocalStorage } from '@portals/shared-frontend/hooks';
import type { OpenAPIV3 } from 'openapi-types';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { HeroContent } from '@/components/HeroPage/HeroContent';
import HeroMenu from '@/components/HeroPage/HeroMenu';
import HeroMobileMenu from '@/components/HeroPage/HeroMobileMenu';
import { TopOfContentWrapper } from '@/pages/api-documentation/components/TopOfContentWrapper';
import { LinkType, methods, SearchItem, TagType } from '@/pages/api-documentation/constants/types';
import ApiReferenceSearch from '@/pages/api-documentation/sections/reference/components/ApiReferenceSearch';
import { ReferenceContentView } from '@/pages/api-documentation/sections/reference/components/ReferenceView/components/ReferenceContentView';

import Menu from './components/Menu';

import { StyledMenu } from '@/pages/api-documentation/sections/reference/components/ReferenceView/index.styles';
import { StyledApiDocumentationPage } from '@/pages/api-documentation/styles/Api.Reference.styles';

interface Props {
  reference: OpenAPIV3.Document;
  dropdown?: ReactNode;
}

const ReferenceView = ({ reference, dropdown }: Props) => {
  /**
   * A deep copy of the reference object before our parsing adds fields such as 'referenceName'
   *
   * This can be removed in the future if the 'dereference' function in shared/common/open-api/resolver.ts
   * is updated to not modify the original object.
   */
  const referenceCopy = useMemo(() => structuredClone(reference), [reference]);

  const {
    info,
    paths,
    tags: refTags,
    components,
    servers,
    security,
  } = useMemo(() => dereference(referenceCopy), [referenceCopy]);

  const [compact, setCompact] = useLocalStorage('api_reference_compact_mode', false);

  const [items, setItems] = useState<string[]>([]);
  const [menuItems, setMenuItems] = useState<Record<string, LinkType[]>>();
  const [tags, setTags] = useState<TagType[] | undefined>();
  const [search, setSearch] = useState<string[] | undefined>();
  const [searchItems, setSearchItems] = useState<SearchItem[] | undefined>();
  const [hasOperationsWithoutTag, setHasOperationsWithoutTag] = useState(false);

  const navigateTo = useCallback((hash = '', jump = false) => {
    // It is important that the url is updated without actual navigation, so that the page does not reload.
    if (globalThis.location.hash !== hash) {
      globalThis.window.history.replaceState(null, '', globalThis.location.pathname + hash);
    }
    if (jump) {
      document.getElementById(hash.replace('#', ''))?.scrollIntoView({ behavior: 'auto' });
    }
  }, []);

  useEffect(() => {
    if (items.length === 0) {
      const allItems: string[] = [];
      const allTags: TagType[] =
        (refTags &&
          refTags.map((tag) => {
            return {
              name: tag.name,
              href: tag.name.replaceAll(' ', '-'),
              description: tag?.description,
            };
          })) ||
        [];
      const allMenuItems: Record<string, LinkType[]> = {};
      const allSearchItems: SearchItem[] = [];

      // ADD ITEMS TO THE DISCOVERY LIST.
      allItems.push('introduction');
      if (components?.securitySchemes) {
        allItems.push('security-schemes');
        Object.keys(components?.securitySchemes).map((schema) => {
          allItems.push(`security-schemes/${schema}`);
        });
      }

      Object.entries(paths).forEach(([pathKey, pathItem]) => {
        if (!pathItem || typeof pathItem === 'string') return;

        methods.forEach((httpMethod) => {
          const operationObject = pathItem[httpMethod];
          if (!operationObject) return;

          const operationTag = operationObject?.tags?.[0];
          if (!operationTag) {
            setHasOperationsWithoutTag(true);
          }
          const operationTagHref = operationTag?.replaceAll(' ', '-');
          if (operationTag && operationTagHref && !allTags.some((tag) => tag.name === operationTag)) {
            allTags.push({
              name: operationTag,
              href: operationTagHref,
            });
          }
          const newMenuItemKey = operationTagHref ?? '';

          const keyPrefix = operationTagHref ? `tags/${newMenuItemKey}` : 'tags';
          const keySuffix = `${httpMethod}${pathKey}`.replaceAll('{', '').replaceAll('}', '');
          const key = `${keyPrefix}/operation/${keySuffix}`;

          allItems.push(keyPrefix, key);
          allSearchItems.push({
            name: keySuffix,
            description: operationObject.summary + ' ' + operationObject.description,
          });

          const newMenuItem = {
            path: keySuffix,
            operator: httpMethod,
            description: operationObject.description ?? '',
            name: operationObject.summary ?? '',
          };

          if (Object.keys(allMenuItems).includes(newMenuItemKey)) {
            allMenuItems[newMenuItemKey] = [...allMenuItems[newMenuItemKey]!, newMenuItem];
          } else {
            allMenuItems[newMenuItemKey] = [newMenuItem];
          }
        });
      });
      setTags(allTags);
      setSearchItems(allSearchItems);
      setMenuItems(allMenuItems);
      setItems(allItems);
    }
  }, [tags, components?.securitySchemes, items, paths, refTags]);

  useEffect(() => {
    if (referenceCopy) {
      setTimeout(() => {
        document.getElementById(globalThis.location.hash.replace('#', ''))?.scrollIntoView({ behavior: 'auto' });
      }, 500);
    }
  }, [referenceCopy]);

  return (
    <>
      <HeroMenu noPadding>
        <StyledMenu>
          <div className="flex flex-col gap-8">
            <ApiReferenceSearch items={searchItems} searchResult={setSearch} />
            {tags && (
              <Menu
                compact={compact}
                components={components}
                items={items}
                menuItems={menuItems}
                mobile={false}
                navigateTo={navigateTo}
                paths={paths}
                search={search}
                tags={tags}
              />
            )}
          </div>
        </StyledMenu>
      </HeroMenu>
      <HeroMobileMenu>
        <StyledMenu>
          <div className="flex flex-col gap-8">
            <ApiReferenceSearch items={searchItems} searchResult={setSearch} />
            {tags && (
              <Menu
                compact={compact}
                components={components}
                items={items}
                menuItems={menuItems}
                mobile
                navigateTo={navigateTo}
                paths={paths}
                search={search}
                tags={tags}
              />
            )}
          </div>
        </StyledMenu>
      </HeroMobileMenu>
      <HeroContent>
        <TopOfContentWrapper>
          <Switch
            checked={compact}
            label="Compact Mode"
            labelPosition="left"
            onChange={({ checked }) => setCompact(checked)}
          />
          {dropdown}
        </TopOfContentWrapper>
        <StyledApiDocumentationPage>
          <ReferenceContentView
            compact={compact}
            components={components}
            hasOperationsWithoutTag={hasOperationsWithoutTag}
            info={info}
            paths={paths}
            security={security}
            servers={servers}
            tags={tags}
          />
        </StyledApiDocumentationPage>
      </HeroContent>
    </>
  );
};

export default ReferenceView;
